import * as React from 'react';
import { VtxButton, VtxIcon, VtxTooltip } from '@vertexinc/vtx-ui-react-component-library';
import FileZoneProps from './FileZoneProps.interface';
import { FileType, formatFileSize, hashFile } from '../..';
import { compareFiles } from './functions';
import './filezone.scss';

const FileZone = ({ files, statusMap, onDeleteFile }: FileZoneProps): JSX.Element => {
  // Submit the Files
  const onDelete = (event: React.MouseEvent<HTMLElement>, file: File): void => {
    // Prevent the File Open Dialog from Opening
    event.stopPropagation();

    onDeleteFile(file);
  };

  // Get the correct Icon based on the File Type
  const getImageForType = (file: File): JSX.Element => {
    if ((file.type.toLowerCase() === FileType.Zip) || (file.type.toLowerCase() === FileType.Zip2)) {
      return (
        <VtxIcon type="file-zip" fontSize={22} color="blue" />
      );
    }

    if ((file.type.toLowerCase() === FileType.Gif) || (file.type.toLowerCase() === FileType.Jpg) || (file.type.toLowerCase() === FileType.Png)
      || (file.type.toLowerCase() === FileType.Bmp) || (file.type.toLowerCase() === FileType.WebP)) {
      return (
        <VtxIcon type="file-image" fontSize={22} color="blue" />
      );
    }

    return (
      <VtxIcon type="file" fontSize={22} color="blue" />
    );
  };

  // Get the correct Icon based on the File Status
  const getImageForStatus = (file: File): JSX.Element => {
    // eslint-disable-next-line  react/prop-types
    const data: boolean | string = statusMap.get(file.name);

    const fileLoading = (data === undefined);
    const fileLoadedFailed = (typeof (data) === 'string');
    const fileLoadedSuccess = (typeof (data) === 'boolean');

    const tooltip: string = fileLoadedFailed ? data as string : null;

    if (fileLoadedSuccess) {
      return (
        <VtxTooltip title="Success">
          <VtxIcon type="check-circle" fontSize={22} color="green" />
        </VtxTooltip>
      );
    }

    if (fileLoadedFailed) {
      return (
        <VtxTooltip title={tooltip}>
          <VtxIcon type="exclamation-circle" fontSize={22} color="red" />
        </VtxTooltip>
      );
    }

    return (<img src="/images/loading-animation.svg" alt="Loading" />);
  };

  // Get the List of Files to be rendered
  const getFilesList = (fileArray: File[]): JSX.Element[] => {
    const numberFiles: number = fileArray.length;

    // Loop through the Files
    return fileArray.map((file: File) => {
      const fileNameSize = `${file.name} (${formatFileSize(file.size)})`;
      const key: string = hashFile(file);
      const deleteTooltip = `Delete the '${file.name}' file`;

      // Generate the HTML to the File
      return (
        <div className="filezoneContentFile" key={key}>
          <div className="filezoneContentFileStatus">
            {getImageForStatus(file)}
          </div>
          <div className="filezoneContentFileIcon">
            {getImageForType(file)}
          </div>
          <div className="filezoneContentFileName">
            <span>{fileNameSize}</span>
          </div>
          <div className="filezoneContentFileDelete">
            <VtxTooltip title={deleteTooltip}>
              <VtxIcon type="close-circle" fontSize={18} onClick={(event: React.MouseEvent<HTMLElement>): void => onDelete(event, file)} />
            </VtxTooltip>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="filezoneContentFiles">
      {getFilesList(files.sort(compareFiles))}
    </div>
  );
};

export default FileZone;
